import React, { useMemo } from "react";
import { CareCommentTitle } from "../../../../../domain/care_comment/CareCommentTitle";
import { CareRecipientHouseId } from "../../../../../domain/care_recipient/CareRecipientHouseId";
import { DateTime } from "../../../../../domain/fundamental/DateTime";
import { getLabelFromUserAccountRole, UserAccountRole } from "../../../../../domain/user/account/UserAccountRole";
import { UserEmail } from "../../../../../domain/user/UserEmail";
import { UserName } from "../../../../../domain/user/UserName";
import { useCareCommentAnalysisDataList } from "../../../../hooks/admin/care_comment_analysis/useCareCommentAnalysisDataList";
import { v4 as uuidv4 } from "uuid";
import classNames from "classnames";

export const CareCommentAnalysisDataTableBody = React.memo(function CareCommentAnalysisDataTableBody() {
    const { list } = useCareCommentAnalysisDataList();

    return (
        <tbody>
            {list.map((careCommentAnalysisData, index) =>
                <TableCareCommentRow
                    key={uuidv4()}
                    count={index + 1}
                    careCommentAnalysisData={careCommentAnalysisData}
                />
            )}
        </tbody>
    );
});

const TableCareCommentRow = React.memo(function TableCareCommentRow(props: TableCareCommentRowProps) {
    const { count, careCommentAnalysisData } = props;

    const rowSpan = useMemo(() => Math.max(careCommentAnalysisData.userDataList.length, 1), [careCommentAnalysisData]);

    const [firstUser, ...restUsers] = useMemo(() => careCommentAnalysisData.userDataList, [careCommentAnalysisData]);

    const evenNumberColumn = useMemo(() => count % 2 === 0, [count]);

    return (
        <>
            <tr className={classNames({ "gray_filled": evenNumberColumn })}>
                <td rowSpan={rowSpan} className={"number"}>{count}</td>
                <td rowSpan={rowSpan}>
                    {`${careCommentAnalysisData.createdTime.date.format()} ${careCommentAnalysisData.createdTime.time.formatHourMinute()}`}
                </td>
                <td rowSpan={rowSpan}>{careCommentAnalysisData.houseId.value}</td>
                <td rowSpan={rowSpan} className={"title"}>{careCommentAnalysisData.title.format(30)}</td>
                <td rowSpan={rowSpan}>{careCommentAnalysisData.registerCount}</td>
                <TableUserData userData={firstUser} />
            </tr>
            {restUsers.map(user => (
                <tr key={uuidv4()} className={classNames({ "gray_filled": evenNumberColumn })}>
                    <TableUserData userData={user} />
                </tr>
            ))}
        </>
    );
});

const TableUserData = React.memo(function TableUserData(props: TableUserDataProps) {
    const { userData } = props;
    return userData ? (
        <>
            <td>{userData.userName.value}</td>
            <td>{userData.email.value}</td>
            <td>{getLabelFromUserAccountRole(userData.role)}</td>
            <td>
                {userData.readTime ? `${userData.readTime.date.format()} ${userData.readTime.time.formatHourMinute()}` : "-"}
            </td>
        </>
    ) : (
        <>
            <td>-</td>
            <td>-</td>
            <td>-</td>
            <td>-</td>
        </>
    );
});

type TableCareCommentRowProps = {
    count: number,
    careCommentAnalysisData: {
        createdTime: DateTime,
        houseId: CareRecipientHouseId,
        title: CareCommentTitle,
        registerCount: number,
        userDataList: {
            userName: UserName,
            email: UserEmail,
            role: UserAccountRole,
            readTime: DateTime | null
        }[]
    }
}

type TableUserDataProps = {
    userData: {
        userName: UserName,
        email: UserEmail,
        role: UserAccountRole,
        readTime: DateTime | null
    } | undefined | null
}